/**
 * AIHelperTrigger — invisible wrapper that marks a region as "AI-helpable".
 *
 * Uses useClickSelection to detect clicks/taps on data-ai-component elements
 * within its subtree and notifies AIHelperContext when a valid selection is made.
 *
 * When the panel is dismissed, the visual highlight on the clicked element
 * is automatically cleared.
 *
 * Usage:
 *   <AIHelperTrigger puzzleInstanceId={id} gameSlug="cloze" enabled={gameOver}>
 *     <div>...game content with data-ai-* annotated elements...</div>
 *   </AIHelperTrigger>
 */

import React, { useRef, useEffect } from 'react';
import { useClickSelection } from '../../hooks/useClickSelection';
import { useAIHelper } from '../../contexts/AIHelperContext';
import { useAIHelperOnboarding } from '../../hooks/useAIHelperOnboarding';
import AIHelperCoachmark from './AIHelperCoachmark';
import { useGameResultsPresentation } from '../../contexts/GameResultsPresentationContext';
import '../../styles/AIHelper.module.css'; // ensure global highlight + hover styles are loaded

export default function AIHelperTrigger({
  puzzleInstanceId,
  gameSlug,
  enabled = true,
  onboardingReady = true,
  coachmarkSelector,
  children,
}) {
  const containerRef = useRef(null);
  const { selectedText, selection, clearSelection } =
    useClickSelection(containerRef, enabled);
  const { setSelection, isOpen } = useAIHelper();
  const { coachmarkVersion } = useGameResultsPresentation();

  // Onboarding: pulse + coachmark whenever the game-end trigger is enabled
  const { coachmarkTarget, dismissOnboarding } =
    useAIHelperOnboarding(
      containerRef,
      enabled && onboardingReady && coachmarkVersion > 0,
      gameSlug,
      coachmarkSelector,
      coachmarkVersion
    );

  // When a valid click-selection is made, notify the context (opens panel)
  useEffect(() => {
    if (selection && selectedText) {
      setSelection({
        text: selectedText,
        selection,
        puzzleInstanceId,
        gameSlug,
      });
    }
  }, [selection, selectedText, puzzleInstanceId, gameSlug, setSelection]);

  // When the panel closes (isOpen goes false), clear the element highlight
  const prevIsOpenRef = useRef(isOpen);
  useEffect(() => {
    const wasOpen = prevIsOpenRef.current;
    prevIsOpenRef.current = isOpen;

    if (wasOpen && !isOpen) {
      clearSelection();
    }
  }, [isOpen, clearSelection]);

  return (
    <div
      ref={containerRef}
      className={enabled ? 'ai-helper-enabled' : undefined}
      style={{ display: 'contents' }}
    >
      {children}
      {coachmarkTarget && (
        <AIHelperCoachmark
          targetElement={coachmarkTarget}
          onDismiss={dismissOnboarding}
        />
      )}
    </div>
  );
}
